import {
  FilterData,
  MetadataFieldType,
  MetadataFilterValue,
  MetadataValue,
  NumericFilter,
  SearchRequest,
} from '@/api/generatedApi';
import { SelectedFilters } from '@/components/FilterData/types';
import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';

export const ALLOWED_IMAGE_FORMATS = '.gif,.jpeg,.jpg,.png';

export const EMPTY_SEARCH_FILTERS: SelectedFilters = { extensions: [], labels: [], folders: [], metadata: {} };

export type ApiFilters = NonNullable<SearchRequest['filters']>;

const isNumericFilter = (value: unknown): value is NumericFilter => {
  if (typeof value !== 'object' || value === null) return false;
  return 'gt' in value || 'gte' in value || 'lt' in value || 'lte' in value;
};

const normalizeMetadataValueByType = (
  value: unknown,
  fieldName: string,
  metadataFields: { name: string; type: MetadataFieldType }[]
): MetadataFilterValue => {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }

  const field = metadataFields.find(f => f.name === fieldName);
  if (!field) {
    return value as MetadataValue;
  }

  switch (field.type) {
    case MetadataFieldType.Text: {
      return String(value);
    }
    case MetadataFieldType.Number: {
      if (isNumericFilter(value)) {
        return value;
      }
      if (typeof value === 'string') {
        const numberValue = Number(value);
        return Number.isNaN(numberValue) ? 0 : numberValue;
      }
      return typeof value === 'number' ? value : 0;
    }
    case MetadataFieldType.Boolean: {
      if (typeof value === 'string') {
        const lower = value.toLowerCase();
        if (lower === 'true') return true;
        if (lower === 'false') return false;
      }
      return Boolean(value);
    }
    default: {
      return String(value);
    }
  }
};

export const getMetadataFiltersQueryParameter = (selectedFilters: SelectedFilters): string =>
  Object.keys(selectedFilters.metadata).length > 0 ? JSON.stringify(selectedFilters.metadata) : '';

export const normalizeFolderPath = (folderPath: string): string =>
  folderPath.endsWith('/') ? folderPath : `${folderPath}/`;

export const normalizeFolderPaths = (folders: string[]): string[] =>
  folders.map(folderPath => normalizeFolderPath(folderPath));

export const normalizeSelectedFilters = (selectedFilters?: SelectedFilters): ApiFilters => {
  const filters = selectedFilters ?? EMPTY_SEARCH_FILTERS;
  return {
    ...filters,
    folders: normalizeFolderPaths(filters.folders),
  };
};

export const normalizeSearchQuery = (searchQuery?: string): string | undefined => {
  return searchQuery && searchQuery.length > 0 ? searchQuery : undefined;
};

export const normalizePage = (page?: string | number): number => {
  if (page === undefined || page === null) return 1;
  const numberPage = typeof page === 'string' ? Number(page) : page;
  return Number.isNaN(numberPage) || numberPage < 1 ? 1 : numberPage;
};

export const normalizePerPage = (
  perPage?: string | number,
  defaultPerPage: number = DATA_GRID_LAZY_PAGE_SIZE
): number => {
  if (perPage === undefined || perPage === null) return defaultPerPage;
  const numberPerPage = typeof perPage === 'string' ? Number(perPage) : perPage;
  return Number.isNaN(numberPerPage) || numberPerPage < 1 ? defaultPerPage : numberPerPage;
};

const arrayToQueryParameter = (array: string[]): string => (array.length > 0 ? `["${array.join('","')}"]` : '');

export const getFoldersQueryParameter = (selectedFilters: SelectedFilters): string =>
  arrayToQueryParameter(normalizeFolderPaths(selectedFilters.folders));

export const getExtensionsQueryParameter = (selectedFilters: SelectedFilters): string =>
  arrayToQueryParameter(selectedFilters.extensions);

export const getLabelsQueryParameter = (selectedFilters: SelectedFilters): string =>
  arrayToQueryParameter(selectedFilters.labels);

const setParameterIfNotEmpty = (parameters: URLSearchParams, key: string, value: unknown[]) => {
  if (value.length > 0) {
    parameters.set(key, JSON.stringify(value));
  }
};

export const serializeFiltersToURLParameters = (filters: SelectedFilters): URLSearchParams => {
  const parameters = new URLSearchParams();
  setParameterIfNotEmpty(parameters, 'extensions', filters.extensions);
  setParameterIfNotEmpty(parameters, 'labels', filters.labels);
  setParameterIfNotEmpty(parameters, 'folders', filters.folders);
  if (Object.keys(filters.metadata).length > 0) {
    parameters.set('metadata', JSON.stringify(filters.metadata));
  }
  return parameters;
};

const parseArrayParameter = (searchParameters: URLSearchParams, key: string): string[] => {
  const parameter = searchParameters.get(key);
  if (!parameter) return [];
  try {
    const parsed = JSON.parse(parameter);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

export const deserializeFiltersFromURLParameters = (
  searchParameters: URLSearchParams,
  metadataFields: { name: string; type: MetadataFieldType }[] = []
): SelectedFilters => {
  const filters: SelectedFilters = {
    extensions: parseArrayParameter(searchParameters, 'extensions'),
    labels: parseArrayParameter(searchParameters, 'labels'),
    folders: parseArrayParameter(searchParameters, 'folders'),
    metadata: {},
  };

  const metadataParameter = searchParameters.get('metadata');
  if (metadataParameter) {
    try {
      const parsedMetadata = JSON.parse(metadataParameter);
      if (parsedMetadata && typeof parsedMetadata === 'object') {
        const normalizedMetadata: Record<string, MetadataFilterValue> = {};
        for (const [key, value] of Object.entries(parsedMetadata)) {
          normalizedMetadata[key] = normalizeMetadataValueByType(value, key, metadataFields);
        }
        filters.metadata = normalizedMetadata;
      }
    } catch {
      void 0;
    }
  }

  return filters;
};

const sortByCountThenFilter = (a: { count: number; filter: string }, b: { count: number; filter: string }): number => {
  const countDiff = b.count - a.count;
  return countDiff === 0 ? String(a.filter).localeCompare(String(b.filter)) : countDiff;
};

const sortMetadataByCountThenFilter = (
  a: { count: number; filter: MetadataValue },
  b: { count: number; filter: MetadataValue }
): number => {
  const countDiff = b.count - a.count;
  return countDiff === 0 ? String(a.filter).localeCompare(String(b.filter)) : countDiff;
};

const combineFilterArrays = <T extends { filter: string; count: number }>(
  existing: T[] | undefined,
  selected: string[]
): T[] => {
  const existingFilters = existing || [];
  const newFilters = selected
    .filter(item => !existingFilters.some(f => f.filter === item))
    .map(item => ({ filter: item, count: 0 }) as T);
  return [...existingFilters, ...newFilters].sort(sortByCountThenFilter);
};

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) return String(value);
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

export const getCombinedFilters = ({
  filterData,
  selectedFilters,
}: {
  filterData?: FilterData;
  selectedFilters: SelectedFilters;
}): FilterData => {
  const combinedMetadata: Record<string, Array<{ filter: MetadataValue; count: number }>> = {
    ...filterData?.metadata,
  };

  for (const [key, value] of Object.entries(selectedFilters.metadata || {})) {
    const existingValues = combinedMetadata[key] || [];
    const valueString = stringifyValue(value);
    if (!existingValues.some(v => stringifyValue(v.filter) === valueString)) {
      combinedMetadata[key] = [...existingValues, { filter: value as MetadataValue, count: 0 }];
    }
  }

  const sortedMetadataEntries = Object.entries(combinedMetadata)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, values]) => [key, [...values].sort(sortMetadataByCountThenFilter)]);

  return {
    extensions: combineFilterArrays(filterData?.extensions, selectedFilters.extensions || []),
    labels: combineFilterArrays(filterData?.labels, selectedFilters.labels || []),
    folders: combineFilterArrays(filterData?.folders, selectedFilters.folders || []),
    metadata: Object.fromEntries(sortedMetadataEntries),
  };
};
